import { useState, useEffect, useRef, useCallback } from 'react'

interface UseTranscriptionOptions {
  transcriptionServerUrl: string
  onPartialTranscript?: (text: string) => void
  onFinalTranscript?: (text: string) => void
  onError?: (error: string) => void
}

export const useTranscription = ({
  transcriptionServerUrl,
  onFinalTranscript,
  onError
}: UseTranscriptionOptions) => {
  const [isConnected, setIsConnected] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [currentTranscript, setCurrentTranscript] = useState('')
  
  const wsRef = useRef<WebSocket | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null)
  const audioStreamRef = useRef<MediaStream | null>(null)
  const isMountedRef = useRef<boolean>(true)

  const connect = useCallback(() => {
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      console.log('WebSocket already connected or connecting, skipping new connection')
      return // Already connected or connecting
    }

    // Clean up any existing connection first
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }

    try {
      console.log('Creating new WebSocket connection to:', transcriptionServerUrl)
      const ws = new WebSocket(transcriptionServerUrl)
      wsRef.current = ws

      ws.onopen = () => {
        if (isMountedRef.current) {
          console.log('WebSocket onopen event triggered')
          setIsConnected(true)
          console.log('Connected to transcription server - isConnected set to true')
        }
      }

      ws.onmessage = (event) => {
        try {
          // The transcription server sends JSON with transcript field
          const data = JSON.parse(event.data)
          const transcription = data.transcript
          if (transcription && transcription.trim()) {
            console.log('Received transcription:', transcription)
            setCurrentTranscript(transcription)
            onFinalTranscript?.(transcription)
          }
        } catch (error) {
          // Fallback: try to parse as plain text
          const transcription = event.data
          if (transcription && transcription.trim()) {
            console.log('Received transcription (plain text):', transcription)
            setCurrentTranscript(transcription)
            onFinalTranscript?.(transcription)
          }
        }
      }

      ws.onclose = (event) => {
        if (isMountedRef.current) {
          setIsConnected(false)
          console.log('Disconnected from transcription server:', event.code, event.reason)
          if (event.code !== 1000) { // Not a normal closure
            onError?.('Transcription connection lost')
            // Attempt to reconnect after a delay
            setTimeout(() => {
              if (isMountedRef.current && (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED)) {
                console.log('Attempting to reconnect to transcription server...')
                connect()
              }
            }, 3000)
          }
        }
      }

      ws.onerror = (error) => {
        console.error('Transcription WebSocket error:', error)
        onError?.('Transcription connection error')
      }

    } catch (error) {
      console.error('Failed to create transcription WebSocket connection:', error)
      onError?.('Failed to connect to transcription server')
    }
  }, [transcriptionServerUrl, onFinalTranscript, onError])

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      console.log('Disconnecting WebSocket')
      // Only close if the connection is actually open
      if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
        wsRef.current.close()
      }
      wsRef.current = null
    }
    setIsConnected(false)
  }, [])

  const startTranscription = useCallback(async () => {
    console.log('startTranscription called, isConnected:', isConnected)
    
    if (!isConnected) {
      console.error('Transcription WebSocket not connected')
      onError?.('Transcription service not connected')
      return
    }

    try {
      console.log('Requesting microphone access...')
      // Get audio stream with specific settings like the working HTML code
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        } 
      })

      console.log('Microphone access granted, stream:', stream)
      audioStreamRef.current = stream

      // Create AudioContext with 16kHz sample rate like the working code
      console.log('Creating AudioContext...')
      const audioContext = new AudioContext({
        sampleRate: 16000
      })
      audioContextRef.current = audioContext
      console.log('AudioContext created, state:', audioContext.state)

      // Resume AudioContext if suspended (required by modern browsers)
      if (audioContext.state === 'suspended') {
        console.log('Resuming suspended AudioContext...')
        await audioContext.resume()
        console.log('AudioContext resumed, new state:', audioContext.state)
      }

      // Create analyser for audio visualization (optional)
      const analyser = audioContext.createAnalyser()
      const source = audioContext.createMediaStreamSource(stream)
      source.connect(analyser)
      console.log('Audio source connected to analyser')

      // Create script processor for raw audio data processing
      console.log('Creating ScriptProcessor...')
      const bufferSize = 4096
      const scriptProcessor = audioContext.createScriptProcessor(bufferSize, 1, 1)
      scriptProcessorRef.current = scriptProcessor
      
      // Suppress deprecation warning for now (ScriptProcessorNode is still widely supported)
      console.log('Using ScriptProcessorNode for audio processing (deprecated but functional)')

      scriptProcessor.onaudioprocess = (event) => {
        console.log('Audio process event triggered')
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          // Get raw audio data
          const inputBuffer = event.inputBuffer
          const inputData = inputBuffer.getChannelData(0)
          
          // Convert float32 to int16 for smaller data size and faster transmission
          const int16Data = new Int16Array(inputData.length)
          for (let i = 0; i < inputData.length; i++) {
            int16Data[i] = Math.max(-32768, Math.min(32767, inputData[i] * 32768))
          }
          
          // Send raw PCM data as ArrayBuffer
          try {
            wsRef.current.send(int16Data.buffer)
            // Log every 100th packet to avoid spam
            if (Math.random() < 0.01) {
              console.log('Sending audio data:', int16Data.buffer.byteLength, 'bytes')
            }
          } catch (error) {
            console.error('Error sending audio data:', error)
            onError?.('Failed to send audio data')
          }
        }
      }

      console.log('Connecting audio nodes...')
      source.connect(scriptProcessor)
      scriptProcessor.connect(audioContext.destination)
      console.log('Audio nodes connected successfully')

      console.log('Setting isTranscribing to true...')
      setIsTranscribing(true)
      console.log('Recording started for transcription - Audio stream active:', stream.active)

    } catch (error) {
      console.error('Failed to start audio recording for transcription:', error)
      onError?.('Failed to start audio recording')
      setIsTranscribing(false)
    }
  }, [isConnected, onError])

  const stopTranscription = useCallback(() => {
    if (audioContextRef.current) {
      audioContextRef.current.close()
      audioContextRef.current = null
    }

    if (scriptProcessorRef.current) {
      scriptProcessorRef.current.disconnect()
      scriptProcessorRef.current = null
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop())
      audioStreamRef.current = null
    }

    setIsTranscribing(false)
    console.log('Transcription recording stopped')
  }, [])

  useEffect(() => {
    isMountedRef.current = true
    
    // Add a small delay to prevent rapid connect/disconnect cycles
    const timeoutId = setTimeout(() => {
      if (isMountedRef.current) {
        console.log('useEffect: Attempting to connect to transcription server...')
        connect()
      }
    }, 500) // Increased delay to ensure component is fully mounted
    
    return () => {
      isMountedRef.current = false
      clearTimeout(timeoutId)
      disconnect()
    }
  }, [transcriptionServerUrl]) // Connect when URL changes

  return {
    isConnected,
    isTranscribing,
    currentTranscript,
    startTranscription,
    stopTranscription,
    connect,
    disconnect
  }
}